import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GB, UA, RU, ES, PT } from "country-flag-icons/react/3x2";
import { getUserRegistration, postChangeAppLanguage } from "../../api/mainApi";

const LanguageSwitcher = ({ tg, setLanguage, setGuideCompleted }) => {
  const { i18n, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const isCisRegion = useMemo(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const cisTimezones = [
        "Europe/Moscow", "Europe/Minsk", "Europe/Volgograd", "Europe/Samara",
        "Asia/Yekaterinburg", "Asia/Omsk", "Asia/Novosibirsk", "Asia/Krasnoyarsk",
        "Asia/Irkutsk", "Asia/Yakutsk", "Asia/Vladivostok", "Asia/Magadan",
        "Asia/Kamchatka", "Asia/Almaty", "Asia/Tashkent", "Asia/Bishkek",
        "Asia/Dushanbe", "Asia/Yerevan", "Asia/Baku", "Asia/Ashgabat",
        "Asia/Tbilisi"
      ];

      return cisTimezones.some(zone => userTimezone.includes(zone));
    } catch (e) {
      return false;
    }
  }, []);

  useEffect(() => {
    const fetchUserRegistration = async () => {
      if (!tg.initData) return;
      try {
        const data = await getUserRegistration();
        const isGuideCompleted = Boolean(data.guide_completed);
        localStorage.setItem("guideCompleted", isGuideCompleted.toString());
        setGuideCompleted(isGuideCompleted);
      } catch (error) {}
    };

    fetchUserRegistration();
  }, [tg.initData, setGuideCompleted]);

  const allLanguages = [
    { code: "en", Flag: GB, alt: "English" },
    { code: "uk", Flag: UA, alt: "Українська" },
    { code: "ru", Flag: RU, alt: "Русский" },
    { code: "es", Flag: ES, alt: "Español" },
    { code: "pt", Flag: PT, alt: "Português" },
  ];

  const availableLanguages = allLanguages.filter(lang => {
    if (lang.code === 'ru') {
      return isCisRegion;
    }
    return true;
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng");
    
    let initialLanguage =
      savedLanguage ||
      tg.initDataUnsafe?.user?.language_code ||
      navigator.language.split("-")[0];

    const supportedCodes = availableLanguages.map(l => l.code);

    if (!supportedCodes.includes(initialLanguage)) {
        initialLanguage = "en"; 
    }

    setSelectedLanguage(initialLanguage);
    if (initialLanguage) {
      i18n.changeLanguage(initialLanguage);
    }
  }, [i18n, tg.initDataUnsafe?.user?.language_code, isCisRegion]);

  const handleLanguageChange = (code) => {
    setSelectedLanguage(code);
    i18n.changeLanguage(code);
  };

  const handleSave = () => {
    if (selectedLanguage) {
      postChangeAppLanguage(
        selectedLanguage
      );
      i18n.changeLanguage(selectedLanguage);
      localStorage.setItem("i18nextLng", selectedLanguage);
      setLanguage(selectedLanguage);
    }
  };

  return (
    <div className="flex flex-col h-dvh w-full bg-transparent">
      <div className="flex-grow flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-8 text-center">
          {t("languageSwitcher.selectLanguage")}
        </h2>
        
        <div className="w-full max-w-md space-y-3">
          {availableLanguages.map(({ code, Flag, alt }) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`w-full flex items-center p-4 rounded-xl border-2 transition-all duration-200 relative group overflow-hidden ${
                selectedLanguage === code
                  ? "border-cyan-500 bg-cyan-500/10 shadow-lg scale-[1.02]"
                  : "border-gray-200/20 bg-white/5 hover:bg-white/10 hover:border-white/30"
              }`}
            >
              <div className="w-10 h-7 rounded-md overflow-hidden shadow-sm flex-shrink-0">
                <Flag className="w-full h-full object-cover" />
              </div>
              
              <span className={`ml-4 text-lg font-medium ${
                selectedLanguage === code ? "text-cyan-400" : "text-gray-200"
              }`}>
                {alt}
              </span>

              {selectedLanguage === code && (
                <div className="ml-auto w-3 h-3 bg-cyan-500 rounded-full shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
              )}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={handleSave}
        className={`block text-center text-3xl font-bold rounded-lg py-2 w-full mb-4 flex-shrink-0 ${
          !selectedLanguage
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move"
        }`}
        disabled={!selectedLanguage}
      >
        {t("languageSwitcher.save")}
      </button>
    </div>
  );
};

export default LanguageSwitcher;